"use client";

import { useEffect } from "react";
import { useRecord } from "@/lib/useRecord";

const LAST_RELOAD_KEY = "chunk_load_reload_at";
const MIN_RELOAD_INTERVAL_MS = 60 * 1000;

const isChunkLoadError = (error: unknown): boolean =>
  error instanceof Error && error.name === "ChunkLoadError";

const reloadOnce = () => {
  try {
    const lastReload = Number(sessionStorage.getItem(LAST_RELOAD_KEY));
    // A chunk that is still missing after a fresh load is a real outage, not
    // a stale tab; don't loop.
    if (lastReload && Date.now() - lastReload < MIN_RELOAD_INTERVAL_MS) {
      return;
    }
    sessionStorage.setItem(LAST_RELOAD_KEY, String(Date.now()));
  } catch {
    // Without storage the throttle can't survive a reload; don't risk a loop.
    return;
  }
  window.location.reload();
};

/**
 * Reloads the page when a JS chunk from a replaced deploy fails to load.
 * Skipped while recording or finalizing: a reload would stop the
 * MediaRecorder mid-meeting or cut off the upload completion.
 */
export const useChunkLoadReload = () => {
  useEffect(() => {
    const handle = (error: unknown) => {
      const { isRecording, isFinalizing } = useRecord.getState();
      if (!isChunkLoadError(error) || isRecording || isFinalizing) {
        return;
      }
      reloadOnce();
    };

    const onError = (event: ErrorEvent) => handle(event.error);
    const onRejection = (event: PromiseRejectionEvent) => handle(event.reason);

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);
};
